/**
 * Maps Service - OpenStreetMap API Integration
 * Uses backend proxy for Nominatim geocoding and Overpass POI queries
 */

import { API_BASE_URL } from '../config/api';
import { getStoredToken } from './authService';

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = getStoredToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

export interface GeocodeResult {
  place_id: number;
  lat: string;
  lon: string;
  display_name: string;
  type: string;
  importance: number;
}

export interface ReverseGeocodeResult {
  place_id: number;
  lat: string;
  lon: string;
  display_name: string;
  address?: {
    house_number?: string;
    road?: string;
    suburb?: string;
    city?: string;
    state?: string;
    postcode?: string;
    country?: string;
  };
}

export interface POIResult {
  id: number;
  lat: number;
  lon: number;
  tags: {
    name?: string;
    amenity?: string;
    shop?: string;
    tourism?: string;
    [key: string]: string | undefined;
  };
}

export interface POICategory {
  id: string;
  label: string;
  query: string;
}

export const POI_CATEGORIES: POICategory[] = [
  { id: 'restaurant', label: 'Restaurants', query: 'amenity=restaurant' },
  { id: 'cafe', label: 'Cafes', query: 'amenity=cafe' },
  { id: 'bar', label: 'Bars', query: 'amenity=bar' },
  { id: 'hotel', label: 'Hotels', query: 'tourism=hotel' },
  { id: 'hospital', label: 'Hospitals', query: 'amenity=hospital' },
  { id: 'pharmacy', label: 'Pharmacies', query: 'amenity=pharmacy' },
  { id: 'bank', label: 'Banks', query: 'amenity=bank' },
  { id: 'school', label: 'Schools', query: 'amenity=school' },
  { id: 'park', label: 'Parks', query: 'leisure=park' },
  { id: 'supermarket', label: 'Supermarkets', query: 'shop=supermarket' },
  { id: 'gas_station', label: 'Gas Stations', query: 'amenity=fuel' },
  { id: 'parking', label: 'Parking', query: 'amenity=parking' },
];

class MapsService {
  /**
   * Search for locations by name
   */
  async searchLocation(query: string, limit: number = 10): Promise<GeocodeResult[]> {
    try {
      const url = `${API_BASE_URL}/maps/search?q=${encodeURIComponent(query)}&limit=${limit}`;
      const response = await fetch(url, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Geocoding request failed' }));
        throw new Error(errorData.message || 'Geocoding request failed');
      }

      const result = await response.json();
      return result.data as GeocodeResult[];
    } catch (error) {
      console.error('Error searching location:', error);
      throw error;
    }
  }

  /**
   * Reverse geocode coordinates to address
   */
  async reverseGeocode(lat: number, lng: number): Promise<ReverseGeocodeResult> {
    try {
      // Validate coordinates
      if (isNaN(lat) || isNaN(lng)) {
        throw new Error('Invalid coordinates');
      }

      if (lat < -90 || lat > 90) {
        throw new Error('Latitude must be between -90 and 90');
      }

      if (lng < -180 || lng > 180) {
        throw new Error('Longitude must be between -180 and 180');
      }

      const url = `${API_BASE_URL}/maps/reverse?lat=${lat}&lon=${lng}`;
      const response = await fetch(url, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Reverse geocoding request failed' }));
        throw new Error(errorData.message || 'Reverse geocoding request failed');
      }

      const result = await response.json();
      return result.data as ReverseGeocodeResult;
    } catch (error) {
      console.error('Error reverse geocoding:', error);
      throw error;
    }
  }

  /**
   * Search for POIs near a location
   */
  async searchPOIs(
    lat: number,
    lng: number,
    radius: number = 1000,
    categories: string[] = []
  ): Promise<POIResult[]> {
    try {
      // Validate coordinates
      if (isNaN(lat) || isNaN(lng)) {
        throw new Error('Invalid coordinates');
      }

      if (lat < -90 || lat > 90) {
        throw new Error('Latitude must be between -90 and 90');
      }

      if (lng < -180 || lng > 180) {
        throw new Error('Longitude must be between -180 and 180');
      }

      const url = `${API_BASE_URL}/maps/pois`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          lat,
          lon: lng,
          radius,
          categories,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'POI search request failed' }));
        throw new Error(errorData.message || 'POI search request failed');
      }

      const result = await response.json();
      return result.data as POIResult[];
    } catch (error) {
      console.error('Error searching POIs:', error);
      throw error;
    }
  }

  /**
   * Format address from reverse geocode result
   */
  formatAddress(result: ReverseGeocodeResult): string {
    if (result.display_name) {
      return result.display_name;
    }

    if (result.address) {
      const addr = result.address;
      const parts = [
        addr.house_number,
        addr.road,
        addr.suburb,
        addr.city,
        addr.state,
        addr.postcode,
        addr.country,
      ].filter(Boolean);

      return parts.join(', ');
    }

    return `${result.lat}, ${result.lon}`;
  }
}

export const mapsService = new MapsService();

